import {
  createElement,
  ElementType,
  type ReactNode,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react"
import { useAppDispatch, useAppSelector } from "~/ui-rtk/app/hooks"
import {
  selectDrawerContent,
  selectIsOpen,
} from "~/ui-rtk/app/selectors/drawer.selector"
import { setContent, setIsOpen } from "~/ui-rtk/app/slices/drawer.slice"
import { DRAWER_TYPE } from "~/ui-rtk/constants/drawer"
import { useClickOutside } from "~/ui-rtk/shared/hooks"
import {
  AdCommentsDrawer,
  CampaingAdSetAdDetails,
  ExperimentsFormDrawer,
  ProductDetails,
} from "~/ui-rtk/components/ui/drawer"
import { CloseSvg } from "~/ui-rtk/components/ui/svg/essentials"

import { cn } from "~/ui-rtk/utils/tailwind-utils"

import "./drawer.scss"
import Alert from "../../ui/common/Alert"
import useSystemBanner from "~/ui-rtk/hooks/useSystemBanner"
import useConnectorUpdateAlert from "~/ui-rtk/hooks/useConnectorUpdateAlert"
import { ConnectorUpdateAlert } from "~/ui-rtk/components/features/connectors/ConnectorUpdateAlert"
import { SosTierAdminAlert } from "~/ui-rtk/components/features/settings/SosTierAdminAlert"
import { StaleTierAdminAlert } from "~/ui-rtk/components/features/settings/StaleTierAdminAlert"

const DRAWER_COMPONENTS: Record<DRAWER_TYPE, ElementType> = {
  CampaingAdSetAdDetails,
  ExperimentsForm: ExperimentsFormDrawer,
  ProductDetails,
  AdComments: AdCommentsDrawer,
}

/** daisyUI's drawer slide; content is unmounted once it has finished. */
const DRAWER_CLOSE_ANIMATION_MS = 300

export default function WithDrawer({ children }: PropsWithChildren) {
  const { isOpen } = useAppSelector(selectIsOpen)
  const { drawerContent } = useAppSelector(selectDrawerContent)
  const dispatch = useAppDispatch()
  const { enabled, payload } = useSystemBanner()
  const {
    hasUpdates,
    updateCount,
    connectorsNeedingUpdate,
    recommendedCategory,
  } = useConnectorUpdateAlert()
  const contentRef = useRef<HTMLDivElement>(null)

  const onClose = useCallback(() => {
    dispatch(setIsOpen(false))
  }, [dispatch, setIsOpen])

  // Drawer content stays mounted through the close animation (it only slides
  // off-screen), so any playing <video> would keep playing audibly. Pause
  // them on close.
  useEffect(() => {
    if (!isOpen) {
      contentRef.current
        ?.querySelectorAll("video")
        .forEach(video => video.pause())
    }
  }, [isOpen])

  // Once the slide-out is over, drop the content so the drawer component
  // unmounts. Otherwise the next open reuses the instance and whatever it
  // held — picker scope and search, an expanded color card, a latched row —
  // shows up under the new product. The X / click-outside path only flipped
  // isOpen; useDrawer().closeDrawer already clears the content itself.
  useEffect(() => {
    if (isOpen || !drawerContent) return undefined
    const timer = window.setTimeout(
      () => dispatch(setContent(null)),
      DRAWER_CLOSE_ANIMATION_MS,
    )
    return () => window.clearTimeout(timer)
  }, [isOpen, drawerContent, dispatch])

  useClickOutside(contentRef, (event: MouseEvent) => {
    if (!isOpen) {
      return
    }

    // Modals (e.g. the Tags editor) render in a Headless UI portal at the
    // document root, outside the drawer DOM. A click inside such a dialog must
    // not be treated as an outside-click that closes the drawer.
    const target = event.target as HTMLElement | null
    if (target?.closest('[role="dialog"]')) {
      return
    }

    if (drawerContent?.clickSourceId) {
      const sourceElement = document.getElementById(
        drawerContent?.clickSourceId,
      )
      if (
        sourceElement &&
        sourceElement.contains(event.target as HTMLElement)
      ) {
        return
      }
    }
    onClose()
  })

  // Keyed by what was opened: a different row of the same drawer type gets a
  // fresh instance instead of inheriting the previous one's state.
  const drawerRenderedContent = useMemo(
    () =>
      !drawerContent ? (
        <></>
      ) : (
        createElement(
          DRAWER_COMPONENTS[DRAWER_TYPE[drawerContent.type] as DRAWER_TYPE],
          {
            key: `${drawerContent.type}:${JSON.stringify(drawerContent.props ?? {})}`,
            ...(drawerContent.props as Record<string, unknown>),
          },
        )
      ),
    [drawerContent],
  )

  return (
    <div className={`drawer drawer-end ${isOpen ? "drawer-open" : ""} h-full`}>
      <input id="my-drawer" type="checkbox" className="drawer-toggle" />
      <div
        className={cn(
          "drawer-content transition-all overflow-y-auto scrollbar h-full",
          {
            "opacity-75": isOpen,
          },
        )}
      >
        {enabled && (
          <div className="p-4">
            <Alert
              // title={payload?.title}
              content={
                (payload as Record<string, unknown>)?.description as ReactNode
              }
              variant={
                (payload as Record<string, unknown>)?.variant as
                  | "info"
                  | "warning"
                  | "success"
                  | "danger"
              }
            />
          </div>
        )}
        <ConnectorUpdateAlert
          hasUpdates={hasUpdates}
          updateCount={updateCount}
          connectorsNeedingUpdate={connectorsNeedingUpdate}
          recommendedCategory={recommendedCategory}
        />
        <SosTierAdminAlert />
        <StaleTierAdminAlert />
        {children}
      </div>
      <div
        ref={contentRef}
        className={cn(
          "drawer-side !fixed top-0 right-0 bottom-0 left-[55%] z-50",
          isOpen && "bg-basic-black",
        )}
      >
        <label
          htmlFor="my-drawer"
          aria-label="close sidebar"
          className="drawer-overlay"
        ></label>
        <div className="flex items-start p-5 mt-13 w-full border-l border-l-basic-blue h-[calc(100vh-55px)]">
          {drawerRenderedContent}
          <button onClick={onClose} className="-ml-5">
            <CloseSvg size={22} />
          </button>
        </div>
      </div>
    </div>
  )
}
